import React, { useEffect, useRef } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { getPhoneDisplayParts } from '../utils/chatIdentity';
import {
    MARKETING_CONSENT_TEXT,
    POLICY_LINKS,
    SERVICE_CONSENT_TEXT,
    SMS_DISCLOSURE_TEXT,
} from '../config/loginConsent';

// #722/#739/#744: driver login is a split-panel modal. Left: CarFido brand panel (desktop
// only). Right: one login channel at a time, then OTP and required consent after verification.
// Colours come from the shared Kirusa tokens (src/styles/kirusa-tokens.css).

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface';

const POLICY_LINK_PATTERN = new RegExp(`(${POLICY_LINKS.map((link) => link.label).join('|')})`);

export const renderWithPolicyLinks = (text) => text.split(POLICY_LINK_PATTERN).map((part, index) => {
    const link = POLICY_LINKS.find((item) => item.label === part);
    if (!link) return <React.Fragment key={index}>{part}</React.Fragment>;
    return (
        <a
            key={index}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`rounded-sm font-bold text-link underline underline-offset-2 hover:no-underline ${FOCUS_RING}`}
        >
            {part}
        </a>
    );
});

const CARFIDO_LOGO_SRC = '/PNG/carfido-logo-horizontal.png';
const BRAND_PHOTO_WEBP = '/images/login-sunset-suv.webp';
const BRAND_PHOTO_JPG = '/images/login-sunset-suv.jpg';

const BrandPanel = () => (
    <aside className="relative isolate hidden min-w-0 flex-col overflow-hidden bg-surface md:flex" aria-label="CarFido">
        <picture className="absolute inset-0">
            <source srcSet={BRAND_PHOTO_WEBP} type="image/webp" />
            <img
                src={BRAND_PHOTO_JPG}
                alt=""
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover object-[left_40%] lg:object-[left_45%]"
            />
        </picture>
        <div
            className="relative z-10 px-8 pb-20 pt-8 lg:px-12"
            style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(255,255,255,0.84) calc(100% - 64px), rgba(255,255,255,0) 100%)' }}
        >
            <img src={CARFIDO_LOGO_SRC} alt="CarFido" className="h-10 w-auto" />
            <p className="mt-2.5 text-[11px] font-extrabold uppercase tracking-[0.24em] text-fg">
                The trusted companion for your car
            </p>
            <p className="mt-6 max-w-[330px] text-[32px] font-extrabold leading-[1.1] tracking-tight text-fg lg:text-[38px]">
                Expert car care in your pocket.
            </p>
            <p className="mt-3 max-w-[340px] text-[16px] font-medium leading-snug text-fg lg:text-[17px]">
                Get answers, find trusted shops, and keep your car moving.
            </p>
        </div>
    </aside>
);

const PhoneChannelIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="5" y="2" width="14" height="20" rx="3" />
        <path d="M12 18h.01" />
    </svg>
);

const EmailChannelIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2.5" y="4.5" width="19" height="15" rx="3" />
        <path d="m3.5 7 8.5 6 8.5-6" />
    </svg>
);

// Desktop only. On mobile the segmented toggle already names the channel, so
// repeating it with a 44px icon block just eats vertical space — the blurb is
// rendered on its own there instead.
// md:min-h keeps the two desktop columns' labels, inputs and buttons on the same
// baseline even if one blurb wraps to an extra line.
const ChannelHeading = ({ icon, title, blurb }) => (
    <div className="hidden items-start gap-3 md:flex md:min-h-[76px]">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#F9F9F9] text-[#414141]">
            {icon}
        </div>
        <div className="min-w-0">
            <p className="text-[15px] font-black text-[#414141]">{title}</p>
            <p className="mt-1 text-sm font-semibold leading-relaxed text-[#707070]">{blurb}</p>
        </div>
    </div>
);

const MobileChannelBlurb = ({ children }) => (
    <p className="mb-1 text-[13px] font-semibold leading-snug text-black/50 md:hidden">{children}</p>
);

// Shared driver login/profile phone styling; the partner login keeps its existing styling.
export const KIRUSA_PHONE_STYLES = `
    .carfido-login-phone .form-control {
        height: 52px !important;
        color: rgb(var(--color-fg)) !important;
        background-color: rgb(var(--color-surface)) !important;
        border-color: rgb(var(--color-border-control)) !important;
        border-radius: 12px !important;
    }
    .carfido-login-phone .form-control:focus {
        border-color: rgb(var(--color-focus)) !important;
        box-shadow: 0 0 0 2px rgb(var(--color-focus)) !important;
    }
    .carfido-login-phone .form-control::placeholder { color: rgb(var(--color-fg-muted)) !important; }
    .carfido-login-phone .flag-dropdown {
        border-right-color: rgb(var(--color-border-control)) !important;
        border-radius: 12px 0 0 12px !important;
    }
    .carfido-login-phone .selected-flag { border-radius: 12px 0 0 12px !important; }
    .carfido-login-phone .selected-flag:focus-visible {
        outline: 2px solid rgb(var(--color-focus)) !important;
        outline-offset: 2px;
    }
    .carfido-login-phone .flag-dropdown:hover,
    .carfido-login-phone .flag-dropdown.open { background-color: rgb(var(--color-surface-muted)) !important; }
    .carfido-login-phone .selected-flag .arrow { border-top-color: rgb(var(--color-fg)) !important; }
    .carfido-login-phone .selected-flag .arrow.up {
        border-bottom-color: rgb(var(--color-fg)) !important;
        border-top-color: transparent !important;
    }
    .carfido-login-phone-dropdown .country,
    .carfido-login-phone-dropdown .country .country-name { color: rgb(var(--color-fg)) !important; }
    .carfido-login-phone-dropdown .country .dial-code { color: rgb(var(--color-fg-muted)) !important; }
    .carfido-login-phone-dropdown .country:hover,
    .carfido-login-phone-dropdown .country.highlight { background-color: rgb(var(--color-surface-muted)) !important; }
    .carfido-login-phone-dropdown .search-box {
        background: rgb(var(--color-surface)) !important;
        border-color: rgb(var(--color-border-control)) !important;
    }
    .carfido-login-phone-dropdown .search-box:focus {
        border-color: rgb(var(--color-focus)) !important;
        box-shadow: 0 0 0 2px rgb(var(--color-focus)) !important;
    }
`;

// Shared with ProfileSetupModal so the post-login profile step's country picker
// looks exactly like the login one.
export const PHONE_INPUT_STYLES = `
    /* ── Auth modal phone input ── */
    .cardash-phone-container {
        width: 100% !important;
    }

    .cardash-phone-container .form-control {
        width: 100% !important;
        height: 56px !important;
        padding-left: 70px !important;
        padding-right: 16px !important;
        font-size: 16px !important;
        font-weight: 700 !important;
        letter-spacing: 0.01em !important;
        color: #000000 !important;
        background-color: #f9f9f9 !important;
        border: 1px solid rgba(0, 0, 0, 0.1) !important;
        border-radius: 16px !important;
        outline: none !important;
        transition: all 0.2s ease-in-out !important;
        font-family: inherit !important;
        box-shadow: none !important;
    }

    .cardash-phone-container .form-control:focus {
        border-color: #D81F26 !important;
        background-color: #ffffff !important;
        box-shadow: 0 0 0 4px rgba(216,31,38,0.15) !important;
    }

    .cardash-phone-container .form-control::placeholder {
        color: #a2a2a2 !important;
        font-weight: 500 !important;
    }

    .cardash-phone-container .flag-dropdown {
        background-color: transparent !important;
        border: none !important;
        border-right: 1px solid rgba(0, 0, 0, 0.1) !important;
        border-radius: 16px 0 0 16px !important;
        width: 58px !important;
        transition: background-color 0.2s ease-in-out !important;
    }

    .cardash-phone-container .flag-dropdown:hover,
    .cardash-phone-container .flag-dropdown.open {
        background-color: rgba(0, 0, 0, 0.04) !important;
        border-radius: 16px 0 0 16px !important;
    }

    .cardash-phone-container .selected-flag {
        width: 58px !important;
        padding-left: 10px !important;
        background: transparent !important;
        border-radius: 16px 0 0 16px !important;
        display: flex !important;
        align-items: center !important;
        gap: 6px !important;
    }

    .cardash-phone-container .selected-flag .flag {
        position: static !important;
        transform: scale(1.15) !important;
        margin: 0 !important;
        flex-shrink: 0 !important;
    }

    .cardash-phone-container .selected-flag .arrow {
        border-top-color: #414141 !important;
        margin-left: 4px !important;
        flex-shrink: 0 !important;
    }

    .cardash-phone-container .selected-flag .arrow.up {
        border-bottom-color: #414141 !important;
        border-top-color: transparent !important;
    }

    /* ── Dropdown ── */
    .cardash-phone-dropdown {
        border-radius: 20px !important;
        border: 1px solid rgba(0, 0, 0, 0.08) !important;
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12) !important;
        padding: 8px !important;
        margin-top: 4px !important;
        background-color: #ffffff !important;
        width: calc(100vw - 64px) !important;
        max-width: 360px !important;
        max-height: 260px !important;
        z-index: 9999 !important;
        left: 0 !important;
        overflow-x: hidden !important;
    }

    .cardash-phone-dropdown::-webkit-scrollbar { width: 6px; }
    .cardash-phone-dropdown::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.1);
        border-radius: 10px;
    }

    .cardash-phone-dropdown .country {
        border-radius: 10px !important;
        padding: 8px 12px !important;
        font-size: 14px !important;
        font-weight: 600 !important;
        color: #414141 !important;
        display: flex !important;
        align-items: center !important;
        gap: 8px !important;
        transition: background 0.12s ease !important;
    }

    .cardash-phone-dropdown .country .flag { flex-shrink: 0 !important; }

    .cardash-phone-dropdown .country .dial-code {
        color: #6B6B6B !important;
        font-weight: 800 !important;
        min-width: 40px !important;
    }

    .cardash-phone-dropdown .country .country-name {
        color: #414141 !important;
        font-weight: 600 !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
    }

    .cardash-phone-dropdown .country:hover { background-color: #f6f6f6 !important; }
    .cardash-phone-dropdown .country.highlight { background-color: #ededed !important; }

    /* ── Search box inside dropdown ── */
    .cardash-phone-dropdown .search {
        position: sticky !important;
        top: -8px !important;
        z-index: 3 !important;
        display: block !important;
        padding: 10px !important;
        margin: -8px -8px 6px !important;
        background: #ffffff !important;
        border-radius: 20px 20px 0 0 !important;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
    }

    .cardash-phone-dropdown .search-emoji { display: none !important; }

    .cardash-phone-dropdown .search-box {
        box-sizing: border-box !important;
        width: 100% !important;
        height: 38px !important;
        margin: 0 !important;
        border: 1.5px solid #D9D9D9 !important;
        border-radius: 10px !important;
        padding: 0 12px !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        color: #181818 !important;
        outline: none !important;
        font-family: inherit !important;
        background: #F9F9F9 !important;
        transition: border-color 0.2s, box-shadow 0.2s !important;
    }

    .cardash-phone-dropdown .search-box:focus {
        border-color: #D81F26 !important;
        box-shadow: 0 0 0 3px rgba(216,31,38,0.16) !important;
        background: #ffffff !important;
    }

    .cardash-phone-dropdown .search-box::placeholder {
        color: #a2a2a2 !important;
        font-weight: 500 !important;
    }
`;

export const PhoneLinkPrompt = ({ onOpenAuthModal, onDismiss }) => (
    <div className="relative overflow-hidden rounded-[22px] border border-[#d9d9d9] bg-[#F9F9F9] px-3 py-3 shadow-[0_10px_30px_rgba(0,0,0,0.08)] md:px-4 md:py-4">
        <button
            onClick={onDismiss}
            className="absolute right-2.5 top-2.5 md:right-3 md:top-1/2 md:-translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full border border-black/10 bg-white text-black/35 transition hover:text-black z-10 md:h-8 md:w-8"
            aria-label="Dismiss phone login suggestion"
        >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
            </svg>
        </button>

        <div className="flex items-center gap-2.5 pr-8 md:gap-4 md:pr-10">
            <div className="flex min-w-0 flex-1 items-start gap-2.5 md:items-center md:gap-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-[#414141] text-white shadow-sm md:h-11 md:w-11">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="5" y="2" width="14" height="20" rx="3" />
                        <path d="M12 18h.01" />
                    </svg>
                </div>

                <div className="min-w-0 flex-1 pt-0.5 md:pt-0">
                    <p className="text-[8px] font-black uppercase tracking-[0.14em] text-[#D81F26] md:text-[10px] md:tracking-[0.18em]">Phone Login</p>
                    <p className="mt-0.5 truncate text-[11px] font-bold leading-snug text-black sm:text-sm md:text-[15px]">
                        <span className="sm:hidden">Save chats and reports.</span>
                        <span className="hidden sm:inline">Verify your number to save your vehicles, chats, and reports.</span>
                    </p>
                </div>
            </div>

            <div className="shrink-0">
                <button
                    onClick={onOpenAuthModal}
                    className="inline-flex h-8 items-center justify-center rounded-full bg-[#D81F26] px-4 text-[10px] font-black uppercase tracking-[0.12em] text-white shadow-md transition hover:-translate-y-0.5 hover:bg-[#B8181E] active:scale-95 md:h-auto md:px-6 md:py-2.5 md:text-[11px] md:tracking-[0.16em]"
                >
                    Login
                </button>
            </div>
        </div>
    </div>
);

// Shop/Partner (admin) login, unchanged by the driver redesign (#722/#739/#744 cover the
// driver modal only).
const AdminLoginModal = ({
    phoneInput,
    otpInput,
    authError,
    loginNotice,
    authBusy,
    onClose,
    onPhoneInputChange,
    onOtpInputChange,
    onVerifyAdmin,
    onSwitchToPhone,
    phoneNumberInputRef,
    otpInputRef,
}) => {
    const handlePhoneKeyDown = (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        otpInputRef.current?.focus();
    };

    const handleOtpKeyDown = (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        if (!authBusy) onVerifyAdmin();
    };

    const closeModal = (...args) => onClose?.(...args);
    const eyebrow = 'Shop/Partner Login';

    return (
        <div className="animate-dash-overlay-in fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/40 px-3 py-6 backdrop-blur-sm sm:px-4 sm:py-8 md:items-center">
            <style>{PHONE_INPUT_STYLES}</style>
            <div className="animate-dash-modal-in relative mt-[2vh] w-full max-w-md overflow-visible rounded-[28px] bg-white px-5 pb-6 pt-9 shadow-[0_30px_80px_rgba(0,0,0,0.2)] sm:rounded-[32px] sm:px-6 md:my-auto md:translate-x-10 md:px-7 md:pb-7 md:pt-11 lg:translate-x-16">
                <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[32px] overflow-hidden rounded-t-[28px] sm:rounded-t-[32px]" style={{ background: 'linear-gradient(to right, #1F1F1F, #414141 55%, #D81F26)' }}>
                    <div className="absolute inset-x-0 bottom-0 top-[6px] bg-white"></div>
                </div>
                <button
                    onClick={closeModal}
                    className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-black/10 bg-white text-black/40 transition hover:text-black sm:right-5 sm:top-5 sm:h-10 sm:w-10"
                    aria-label="Close login"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                    </svg>
                </button>

                <div className="relative z-10 mb-5 md:mb-6">
                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#D81F26] sm:text-[11px]">{eyebrow}</p>
                    <h2 className="mt-1.5 text-[22px] font-black leading-tight tracking-tight text-black sm:mt-2 sm:text-2xl">
                        Log in to your shop
                    </h2>
                    <p className="mt-2 text-[13px] font-semibold leading-relaxed text-black/60 sm:mt-3 sm:text-sm">
                        Enter your shop phone number and code to open the shop view.
                    </p>
                    {loginNotice && (
                        <div className="mt-3 flex items-start gap-2 rounded-2xl border border-[#f0d9a8] bg-[#fdf6e7] px-3 py-2.5 text-[12.5px] font-semibold leading-relaxed text-[#7a5a1e] sm:text-[13px]">
                            <span>{loginNotice}</span>
                        </div>
                    )}
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.18em] text-black/45">
                            Phone Number
                        </label>
                        <PhoneInput
                            country={'us'}
                            value={phoneInput}
                            onChange={(value, country) => onPhoneInputChange(value, country)}
                            placeholder="908 242 3514"
                            enableSearch={true}
                            searchPlaceholder="Search country or code…"
                            disableSearchIcon={true}
                            disableCountryGuess={true}
                            disableInitialCountryGuess={false}
                            countryCodeEditable={false}
                            containerClass="cardash-phone-container"
                            dropdownClass="cardash-phone-dropdown"
                            inputProps={{
                                ref: phoneNumberInputRef,
                                onKeyDown: handlePhoneKeyDown,
                                id: 'admin-phone-input',
                            }}
                        />
                    </div>

                    <div>
                        <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.18em] text-black/45">
                            Access Code
                        </label>
                        <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            pattern="[0-9]{6}"
                            maxLength={6}
                            value={otpInput}
                            onChange={(e) => onOtpInputChange(e.target.value)}
                            onKeyDown={handleOtpKeyDown}
                            ref={otpInputRef}
                            placeholder="Enter 6-digit code"
                            className="w-full rounded-2xl border border-black/10 bg-neutral-50 px-4 py-4 text-base font-bold tracking-[0.25em] text-black outline-none transition focus:border-[#8eb6dc] focus:bg-white"
                        />
                    </div>

                    {authError && (
                        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
                            {authError}
                        </div>
                    )}

                    <div className="flex gap-2.5 sm:gap-3">
                        <button
                            type="button"
                            onClick={onSwitchToPhone}
                            className="min-h-[48px] flex-1 rounded-full border border-black/10 bg-white px-3 text-[11px] font-black uppercase tracking-[0.08em] text-black transition active:scale-[0.99] sm:px-5 sm:text-xs sm:tracking-[0.18em]"
                        >
                            Phone Login
                        </button>
                        <button
                            type="button"
                            onClick={onVerifyAdmin}
                            disabled={authBusy}
                            className="min-h-[48px] flex-1 rounded-full bg-black px-3 text-[11px] font-black uppercase tracking-[0.08em] text-white transition active:scale-[0.99] disabled:opacity-60 sm:px-5 sm:text-xs sm:tracking-[0.18em]"
                        >
                            {authBusy ? 'Verifying...' : 'Verify'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ErrorMessage = ({ children }) => (
    <div
        role="alert"
        data-testid="auth-error"
        className="flex items-start gap-2 rounded-xl border-l-4 border-danger bg-surface-muted px-3.5 py-3 text-sm font-bold text-danger"
    >
        <svg className="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
        </svg>
        <span>{children}</span>
    </div>
);

const ConsentCheckbox = ({ id, checked, onChange, disabled, children }) => (
    <div className="flex items-start gap-3">
        <input
            id={id}
            type="checkbox"
            checked={checked}
            onChange={(event) => onChange?.(event.target.checked)}
            disabled={disabled}
            className={`mt-0.5 h-[22px] w-[22px] shrink-0 cursor-pointer rounded accent-action disabled:cursor-not-allowed ${FOCUS_RING}`}
        />
        <label htmlFor={id} className="cursor-pointer text-[14px] font-medium leading-relaxed text-fg sm:text-[15px] md:[@media(max-height:700px)]:leading-snug">
            {children}
        </label>
    </div>
);

// Shared introductory copy; the legal checkbox wording stays in config/loginConsent.js.
const isReturningConsent = (reason) => reason === 'updated' || reason === 'existing';
export const getLoginConsentHeading = (reason) => (isReturningConsent(reason) ? 'Welcome back' : 'One last step');

export const getLoginConsentSubtitle = (reason) => (isReturningConsent(reason)
    ? "We've updated our terms. Please review and accept them below to continue using CarFido."
    : 'Please review and accept to finish logging in.');

// #739: the consent step shown after the login code when the user has not accepted the
// current consent version. Both boxes start unchecked.
export const LoginConsentStep = ({
    serviceConsentAccepted = false,
    marketingConsentOptIn = false,
    onServiceConsentChange,
    onMarketingConsentChange,
    onAccept,
    authBusy = false,
    authError = '',
    className = 'mt-5 space-y-4',
}) => {
    const canAccept = serviceConsentAccepted && !authBusy;
    return (
        <div className={className}>
            {/* Required and optional consent stay independent. */}
            <div className="space-y-3" data-testid="login-consent">
                <ConsentCheckbox
                    id="login-consent-service"
                    checked={serviceConsentAccepted}
                    onChange={onServiceConsentChange}
                    disabled={authBusy}
                >
                    {renderWithPolicyLinks(SERVICE_CONSENT_TEXT)}
                </ConsentCheckbox>
                <ConsentCheckbox
                    id="login-consent-marketing"
                    checked={marketingConsentOptIn}
                    onChange={onMarketingConsentChange}
                    disabled={authBusy}
                >
                    {MARKETING_CONSENT_TEXT}
                </ConsentCheckbox>
            </div>

            {authError && <ErrorMessage>{authError}</ErrorMessage>}

            <div>
                <button
                    type="button"
                    onClick={() => canAccept && onAccept?.()}
                    disabled={!canAccept}
                    aria-describedby={serviceConsentAccepted ? undefined : 'login-consent-hint'}
                    className={`flex min-h-[52px] w-full items-center justify-center gap-3 rounded-full text-[17px] font-bold transition active:scale-[0.99] disabled:active:scale-100 ${FOCUS_RING} ${
                        serviceConsentAccepted
                            ? 'bg-action text-action-on hover:shadow-lg disabled:cursor-wait'
                            : 'cursor-not-allowed bg-action-disabled text-action-on'
                    }`}
                >
                    {authBusy ? 'Logging in...' : 'Agree and continue'}
                </button>
                {!serviceConsentAccepted && (
                    <p id="login-consent-hint" className="mt-1.5 text-center text-[12px] font-medium text-fg-muted">
                        Agree to the Terms of Use and Privacy Policy above to continue.
                    </p>
                )}
            </div>
        </div>
    );
};

export const PhoneAuthModal = ({
    isOpen,
    authMode = 'phone',
    authChannel = 'phone',
    authStep,
    phoneInput,
    phoneCountry,
    emailInput = '',
    otpInput,
    pendingPhone,
    pendingEmail = '',
    authError,
    authNotice,
    loginNotice = '',
    authBusy,
    serviceConsentAccepted = false,
    marketingConsentOptIn = false,
    consentReason = '',
    onServiceConsentChange,
    onMarketingConsentChange,
    onAcceptLoginConsent,
    onClose,
    onPhoneInputChange,
    onEmailInputChange,
    onOtpInputChange,
    onSelectChannel,
    onRequestOtp,
    onRequestEmailOtp,
    onVerifyOtp,
    onVerifyAdmin,
    onResendOtp,
    resendSecondsRemaining = 0,
    onResetPhoneStep,
    onSwitchToPhone,
}) => {
    const phoneNumberInputRef = useRef(null);
    const emailAddressInputRef = useRef(null);
    const otpInputRef = useRef(null);
    const isAdminMode = authMode === 'admin';
    const isEmailChannel = authChannel === 'email';
    const isLoginStep = !isAdminMode && authStep === 'phone';
    // #739: shown only when the backend says this user has not accepted the current consent
    // version (after the code is verified, before the session exists).
    const isConsentStep = !isAdminMode && authStep === 'consent';
    const canRequestCode = !authBusy;

    const requestCode = () => {
        if (!canRequestCode) return;
        if (isEmailChannel) onRequestEmailOtp();
        else onRequestOtp();
    };

    const handleFieldKeyDown = (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        requestCode();
    };

    const handleOtpKeyDown = (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        if (authBusy) return;
        if (isAdminMode) onVerifyAdmin();
        else onVerifyOtp();
    };

    // Focus the field for the current step and channel. The entry step is still named
    // "phone" historically; it covers both phone and email.
    useEffect(() => {
        if (!isOpen) return undefined;
        let targetRef = otpInputRef;
        if (isAdminMode || authStep === 'phone') {
            targetRef = !isAdminMode && isEmailChannel ? emailAddressInputRef : phoneNumberInputRef;
        } else if (authStep === 'consent') {
            targetRef = { current: document.getElementById('login-consent-service') };
        }
        const t = window.setTimeout(() => targetRef.current?.focus(), 120);
        return () => window.clearTimeout(t);
    }, [isOpen, isAdminMode, authStep, isEmailChannel]);

    // Escape closes the driver modal like the close button does.
    useEffect(() => {
        if (!isOpen || isAdminMode) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, isAdminMode, onClose]);

    if (!isOpen) return null;

    if (isAdminMode) {
        return (
            <AdminLoginModal
                phoneInput={phoneInput}
                otpInput={otpInput}
                authError={authError}
                loginNotice={loginNotice}
                authBusy={authBusy}
                onClose={onClose}
                onPhoneInputChange={onPhoneInputChange}
                onOtpInputChange={onOtpInputChange}
                onVerifyAdmin={onVerifyAdmin}
                onSwitchToPhone={onSwitchToPhone}
                phoneNumberInputRef={phoneNumberInputRef}
                otpInputRef={otpInputRef}
            />
        );
    }

    const pendingPhoneParts = getPhoneDisplayParts(pendingPhone, phoneCountry);
    const otpSentMessage = authNotice || 'One time code sent to';
    const resendCooldown = Math.max(0, Math.ceil(Number(resendSecondsRemaining) || 0));
    const resendDisabled = authBusy || resendCooldown > 0;
    const resendLabel = resendCooldown > 0 ? `Resend Code in ${resendCooldown}s` : 'Resend Code';
    const verifyOtp = () => onVerifyOtp?.();

    return (
        <div className="animate-dash-overlay-in fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/50 px-3 py-4 backdrop-blur-sm sm:px-6 sm:py-8 md:items-center md:py-4">
            <style>{PHONE_INPUT_STYLES}{KIRUSA_PHONE_STYLES}</style>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="carfido-login-title"
                className="animate-dash-modal-in relative my-auto grid w-full max-w-[460px] overflow-hidden rounded-[24px] bg-surface font-brand text-fg shadow-[0_30px_80px_rgba(0,0,0,0.25)] md:max-h-[calc(100vh-32px)] md:max-w-[1000px] md:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] md:grid-rows-[minmax(0,1fr)] md:rounded-[28px] md:supports-[height:100dvh]:max-h-[calc(100dvh-32px)]"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className={`absolute right-3 top-3 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-surface-muted text-fg transition hover:bg-kirusa-white hover:shadow-md sm:right-5 sm:top-5 md:h-12 md:w-12 ${FOCUS_RING}`}
                    aria-label="Close login"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                    </svg>
                </button>

                <BrandPanel />

                <section className="min-w-0 px-5 pb-9 pt-6 sm:px-8 md:overflow-y-auto md:overscroll-contain md:px-12 md:pt-9 lg:pt-10">
                    {/* Phones skip the brand panel; the logo stays visible here. */}
                    <img src={CARFIDO_LOGO_SRC} alt="CarFido" className="mb-6 h-8 w-auto md:hidden" />

                    <p className="text-[12px] font-extrabold uppercase tracking-[0.2em] text-kirusa-red">
                        Welcome to CarFido
                    </p>
                    <h2 id="carfido-login-title" className="mt-2 pr-10 text-[26px] font-extrabold leading-tight tracking-tight text-fg sm:text-[30px] md:pr-0 lg:text-[32px] md:[@media(max-height:700px)]:text-[28px]">
                        {isLoginStep
                            ? 'Log in to your account'
                            : isConsentStep
                                ? getLoginConsentHeading(consentReason)
                                : 'Enter the code'}
                    </h2>
                    <p className="mt-1.5 text-[15px] font-medium leading-relaxed text-fg-muted sm:text-[16px]">
                        {isLoginStep ? (
                            "Choose how you'd like to receive your login code."
                        ) : isConsentStep ? (
                            getLoginConsentSubtitle(consentReason)
                        ) : (
                            <>
                                {otpSentMessage}{' '}
                                {isEmailChannel ? (
                                    <span className="break-all font-extrabold text-fg">{pendingEmail}</span>
                                ) : (
                                    <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap align-baseline font-extrabold text-fg">
                                        {pendingPhoneParts.countryCode && <span>{pendingPhoneParts.countryCode}</span>}
                                        <span>{pendingPhoneParts.nationalNumber}</span>
                                    </span>
                                )}.
                            </>
                        )}
                    </p>

                    {/* Why the login modal opened (expired session, or an action that needs an
                        account). Without this the user only sees a login box appear with no reason. */}
                    {loginNotice && (
                        <div className="mt-4 flex items-start gap-2 rounded-xl bg-surface-muted px-3.5 py-3 text-[13px] font-semibold leading-relaxed text-fg">
                            <svg className="mt-[2px] h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" />
                                <path d="M12 8v4" />
                                <path d="M12 16h.01" />
                            </svg>
                            <span>{loginNotice}</span>
                        </div>
                    )}

                {authStep === 'phone' ? (
                    <div className="space-y-5">
                        <div className="relative grid grid-cols-2 rounded-2xl border border-black/10 bg-neutral-50 p-1.5">
                            <span
                                aria-hidden="true"
                                className="absolute inset-y-1.5 left-1.5 w-[calc(50%-0.375rem)] rounded-xl bg-black shadow-sm transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
                                style={{ transform: isEmailChannel ? 'translateX(100%)' : 'translateX(0)' }}
                            />
                            {[
                                { key: 'phone', label: 'Phone', icon: <PhoneChannelIcon /> },
                                { key: 'email', label: 'Email', icon: <EmailChannelIcon /> },
                            ].map((channel) => {
                                const isActive = authChannel === channel.key;
                                return (
                                    <button
                                        key={channel.key}
                                        type="button"
                                        aria-pressed={isActive}
                                        onClick={() => onSelectChannel(channel.key)}
                                        className={`relative z-10 flex min-h-[48px] items-center justify-center gap-2.5 rounded-xl text-[16px] font-bold transition-colors [&>svg]:h-[22px] [&>svg]:w-[22px] ${FOCUS_RING} ${
                                            isActive ? 'text-white' : 'text-black/55 hover:text-black'
                                        }`}
                                    >
                                        {channel.icon}
                                        {channel.label}
                                    </button>
                                );
                            })}
                        </div>

                        {isEmailChannel ? (
                            <div key="email" className="animate-dash-channel-in">
                                <ChannelHeading
                                    icon={<EmailChannelIcon />}
                                    title="Login with Email"
                                    blurb="We'll send you a one-time code to verify your email."
                                />
                                <MobileChannelBlurb>
                                    We'll email you a one-time code to verify your address.
                                </MobileChannelBlurb>
                                <label htmlFor="auth-email-input" className="mb-2 mt-4 block text-[11px] font-black uppercase tracking-[0.18em] text-black/45">
                                    Email Address
                                </label>
                                <input
                                    id="auth-email-input"
                                    ref={emailAddressInputRef}
                                    type="email"
                                    inputMode="email"
                                    autoComplete="email"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    value={emailInput}
                                    onChange={(e) => onEmailInputChange(e.target.value)}
                                    onKeyDown={handleFieldKeyDown}
                                    placeholder="you@example.com"
                                    className="h-14 w-full rounded-2xl border border-black/10 bg-[#f9f9f9] px-4 text-base font-bold text-black outline-none transition placeholder:font-medium placeholder:text-gray-400 focus:border-[#D81F26] focus:bg-white focus:ring-4 focus:ring-[#D81F26]/15"
                                />
                            </div>
                        ) : (
                            <div key="phone" className="animate-dash-channel-in">
                                <ChannelHeading
                                    icon={<PhoneChannelIcon />}
                                    title="Login with Phone"
                                    blurb="We'll send you a one-time code to verify your number."
                                />
                                <MobileChannelBlurb>
                                    We'll text you a one-time code to verify your number.
                                </MobileChannelBlurb>
                                <label htmlFor="auth-phone-input" className="mb-2 mt-4 block text-[11px] font-black uppercase tracking-[0.18em] text-black/45">
                                    Phone Number
                                </label>
                                <PhoneInput
                                    country={'us'}
                                    value={phoneInput}
                                    onChange={(value, country) => onPhoneInputChange(value, country)}
                                    placeholder="908 242 3514"
                                    enableSearch={true}
                                    searchPlaceholder="Search country or code..."
                                    disableSearchIcon={true}
                                    disableCountryGuess={true}
                                    disableInitialCountryGuess={false}
                                    countryCodeEditable={false}
                                    containerClass="cardash-phone-container"
                                    dropdownClass="cardash-phone-dropdown"
                                    inputProps={{
                                        ref: phoneNumberInputRef,
                                        onKeyDown: handleFieldKeyDown,
                                        id: 'auth-phone-input',
                                    }}
                                />
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={requestCode}
                            disabled={authBusy}
                            className="w-full rounded-full bg-[#D81F26] py-4 text-[12px] font-black uppercase tracking-[0.16em] text-white transition hover:bg-[#B8181E] active:scale-[0.99] disabled:opacity-60 sm:py-3.5 sm:text-[11px]"
                        >
                            {authBusy ? 'Sending...' : 'Login'}
                        </button>


                        {/* #722: SMS disclosure, full width below both Login buttons on desktop
                            and in both Phone and Email modes on mobile, always the full text.
                            Text only: it records no consent and subscribes no one. */}
                        {!isAdminMode && (
                            <p
                                data-testid="sms-consent-disclaimer"
                                className="mx-auto max-w-[680px] pt-1 text-left text-[12px] font-semibold leading-[1.6] text-black/55 sm:text-[12.5px] sm:leading-[1.65] md:text-center md:text-black/45"
                            >
                                {renderWithPolicyLinks(SMS_DISCLOSURE_TEXT)}
                            </p>
                        )}

                        {authError && (
                            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
                                {authError}
                            </div>
                        )}

                    </div>
                ) : isConsentStep ? (
                    <LoginConsentStep
                        serviceConsentAccepted={serviceConsentAccepted}
                        marketingConsentOptIn={marketingConsentOptIn}
                        onServiceConsentChange={onServiceConsentChange}
                        onMarketingConsentChange={onMarketingConsentChange}
                        onAccept={onAcceptLoginConsent}
                        authBusy={authBusy}
                        authError={authError}
                    />
                ) : (
                    <div className="space-y-4">
                        <div>
                            <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                pattern="[0-9]{6}"
                                maxLength={6}
                                value={otpInput}
                                onChange={(e) => onOtpInputChange(e.target.value)}
                                onKeyDown={handleOtpKeyDown}
                                ref={otpInputRef}
                                placeholder="Enter 6-digit code"
                                className="w-full rounded-2xl border border-black/10 bg-neutral-50 px-4 py-4 text-base font-bold tracking-[0.25em] text-black outline-none transition focus:border-[#D81F26] focus:bg-white"
                            />
                        </div>

                        {authError && <ErrorMessage>{authError}</ErrorMessage>}

                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={onResendOtp}
                                disabled={resendDisabled}
                                aria-label={resendLabel}
                                className="text-[11px] font-black uppercase tracking-[0.16em] text-[#D81F26] transition hover:text-black disabled:opacity-50"
                            >
                                {resendLabel}
                            </button>
                        </div>

                        <div className="flex gap-2.5 sm:gap-3">
                            <button
                                type="button"
                                onClick={onResetPhoneStep}
                                className="min-h-[48px] flex-1 rounded-full border border-black/10 bg-white px-3 text-[11px] font-black uppercase tracking-[0.08em] text-black transition active:scale-[0.99] sm:px-5 sm:text-xs sm:tracking-[0.18em]"
                            >
                                {isEmailChannel ? 'Change Email' : 'Change Number'}
                            </button>
                            <button
                                type="button"
                                onClick={verifyOtp}
                                disabled={authBusy}
                                className="min-h-[48px] flex-1 rounded-full bg-[#D81F26] px-3 text-[11px] font-black uppercase tracking-[0.08em] text-white transition hover:bg-[#B8181E] active:scale-[0.99] disabled:opacity-60 sm:px-5 sm:text-xs sm:tracking-[0.18em]"
                            >
                                {authBusy ? 'Verifying...' : 'Verify Code'}
                            </button>
                        </div>
                    </div>
                )}
                </section>
            </div>
        </div>
    );
};
